import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { GuestRoute } from '@/components/auth/GuestRoute'
import { PermissionRoute } from '@/components/auth/PermissionRoute'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { SessionGuard } from '@/components/auth/SessionGuard'
import { LuxuryDashboardWithProvider } from '@/components/dashboard/LuxuryDashboardContent'
import { AppShell } from '@/components/layout/AppShell'
import { CalendarPage } from '@/pages/CalendarPage'
import { InventoryPage } from '@/pages/InventoryPage'
import { AccountsPage } from '@/pages/AccountsPage'
import { InsightsPage } from '@/pages/InsightsPage'
import { LoginPage } from '@/pages/LoginPage'
import { OnlineBookingsPage } from '@/pages/OnlineBookingsPage'
import { PosPage } from '@/pages/PosPage'
import { BranchSetupPage } from '@/pages/admin/BranchSetupPage'
import { AuditLogPage } from '@/pages/admin/AuditLogPage'
import { CompanySetupPage } from '@/pages/admin/CompanySetupPage'
import { UsersSetupPage } from '@/pages/admin/UsersSetupPage'
import { ImportMastersPage } from '@/pages/masters/ImportMastersPage'
import { PosQuickKeysPage } from '@/pages/masters/PosQuickKeysPage'
import { CatalogMastersPage } from '@/pages/masters/CatalogMastersPage'
import { ChairsPage } from '@/pages/masters/ChairsPage'
import { CustomersPage } from '@/pages/masters/CustomersPage'
import { ProductsPage } from '@/pages/masters/ProductsPage'
import { PackagesPage } from '@/pages/masters/PackagesPage'
import { ServiceCategoriesPage } from '@/pages/masters/ServiceCategoriesPage'
import { ServicesPage } from '@/pages/masters/ServicesPage'
import { StylistsPage } from '@/pages/masters/StylistsPage'
import { StylistCommissionPage } from '@/pages/StylistCommissionPage'
import { StylistWorkspacePage } from '@/pages/StylistWorkspacePage'
import { HqDashboardPage } from '@/pages/HqDashboardPage'
import { PERMISSIONS } from '@/lib/permissions'
import { PublicBookLayout } from '@/components/booking/PublicBookLayout'
import { BookBranchCatalogPage } from '@/pages/book/BookBranchCatalogPage'
import { BookBranchHomePage } from '@/pages/book/BookBranchHomePage'
import { BookHubPage } from '@/pages/book/BookHubPage'
import { BookManagePage } from '@/pages/book/BookManagePage'

const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, '') || undefined

function App() {
  return (
    <BrowserRouter basename={routerBasename}>
      <Routes>
        <Route path="/book" element={<PublicBookLayout />}>
          <Route index element={<BookHubPage />} />
          <Route path="manage/:token" element={<BookManagePage />} />
          <Route path=":slug" element={<BookBranchHomePage />} />
          <Route path=":slug/catalog" element={<BookBranchCatalogPage />} />
        </Route>
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <SessionGuard>
                <AppShell />
              </SessionGuard>
            </ProtectedRoute>
          }
        >
          <Route index element={<LuxuryDashboardWithProvider />} />
          <Route
            path="calendar"
            element={
              <PermissionRoute permission={PERMISSIONS.APPOINTMENTS_MANAGE}>
                <CalendarPage />
              </PermissionRoute>
            }
          />
          <Route
            path="online-bookings"
            element={
              <PermissionRoute permission={PERMISSIONS.APPOINTMENTS_MANAGE}>
                <OnlineBookingsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="pos"
            element={
              <PermissionRoute permission={PERMISSIONS.POS_ACCESS}>
                <PosPage />
              </PermissionRoute>
            }
          />
          <Route
            path="customers"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <CustomersPage />
              </PermissionRoute>
            }
          />
          <Route
            path="stylists"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <StylistsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="inventory"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <InventoryPage />
              </PermissionRoute>
            }
          />
          <Route
            path="accounts"
            element={
              <PermissionRoute permission={PERMISSIONS.ACCOUNTS_ACCESS}>
                <AccountsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="hq"
            element={
              <PermissionRoute permission={PERMISSIONS.HQ_VIEW}>
                <HqDashboardPage />
              </PermissionRoute>
            }
          />
          <Route
            path="reports"
            element={
              <PermissionRoute permission={PERMISSIONS.REPORTS_VIEW}>
                <InsightsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="stylist"
            element={
              <PermissionRoute permission={PERMISSIONS.STYLIST_WORKSPACE}>
                <StylistWorkspacePage />
              </PermissionRoute>
            }
          />
          <Route
            path="workspace/commissions"
            element={
              <PermissionRoute permission={PERMISSIONS.STYLIST_WORKSPACE}>
                <StylistCommissionPage />
              </PermissionRoute>
            }
          />
          <Route
            path="admin/company"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_FULL}>
                <CompanySetupPage />
              </PermissionRoute>
            }
          />
          <Route
            path="admin/branches"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_FULL}>
                <BranchSetupPage />
              </PermissionRoute>
            }
          />
          <Route
            path="admin/users"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_USERS}>
                <UsersSetupPage />
              </PermissionRoute>
            }
          />
          <Route
            path="admin/audit-logs"
            element={
              <PermissionRoute permission={PERMISSIONS.ADMIN_FULL}>
                <AuditLogPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/service-categories"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <ServiceCategoriesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/services"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <ServicesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/stylists"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <StylistsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/chairs"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <ChairsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/customers"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <CustomersPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/products"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <ProductsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/packages"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <PackagesPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/catalog"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <CatalogMastersPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/pos-quick-keys"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <PosQuickKeysPage />
              </PermissionRoute>
            }
          />
          <Route
            path="masters/import"
            element={
              <PermissionRoute permission={PERMISSIONS.MASTERS_MANAGE}>
                <ImportMastersPage />
              </PermissionRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
